import { config } from './config';
import {
  CLIENT_TYPES,
  OAUTH,
  PARTNER_QUERY_ACTIVE,
  PARTNER_QUERY_KEY,
} from './constants';
import { createAuthCode, getSession, clearSession } from './identityAuth';
import { fetchMe } from './identityRegistry';

const REDIRECT_STORAGE_KEY = config.redirectStorageKey;

export interface RedirectContext {
  redirectUri?: string;
  state?: string;
  codeChallenge?: string;
  codeChallengeMethod?: string;
  clientId?: string;
}

export function loadRedirectContext(): RedirectContext {
  const params = new URLSearchParams(window.location.search);
  const fromQuery: RedirectContext = {
    redirectUri: params.get(OAUTH.PARAM.REDIRECT_URI) ?? undefined,
    state: params.get(OAUTH.PARAM.STATE) ?? undefined,
    codeChallenge: params.get('code_challenge') ?? undefined,
    codeChallengeMethod: params.get('code_challenge_method') ?? undefined,
    clientId: params.get('client_id') ?? undefined,
  };

  if (fromQuery.redirectUri) {
    sessionStorage.setItem(REDIRECT_STORAGE_KEY, JSON.stringify(fromQuery));
    return fromQuery;
  }

  try {
    const stored = sessionStorage.getItem(REDIRECT_STORAGE_KEY);
    if (stored) return JSON.parse(stored) as RedirectContext;
  } catch {
    sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
  }

  return {};
}

export function clearRedirectContext(): void {
  sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
}

function isCustomScheme(uri: string): boolean {
  return config.allowedRedirectSchemes.some((scheme) => uri.startsWith(scheme));
}

function isAllowedHttpsRedirect(uri: string): boolean {
  try {
    const url = new URL(uri);
    if (url.protocol !== 'https:' && !url.hostname.startsWith('localhost')) return false;
    return config.allowedRedirectOrigins.some((origin) => uri.startsWith(origin));
  } catch {
    return false;
  }
}

function requirePkce(ctx: RedirectContext): void {
  if (!ctx.codeChallenge || (ctx.codeChallengeMethod ?? 'S256') !== 'S256') {
    throw new Error('PKCE code_challenge (S256) is required for redirect handoff');
  }
  if (!ctx.clientId) {
    throw new Error('client_id is required for redirect handoff');
  }
}

async function buildCodeRedirect(uri: string, ctx: RedirectContext, authCodePath = '/auth/code'): Promise<string> {
  requirePkce(ctx);
  const session = getSession();
  if (!session) {
    throw new Error('Not authenticated');
  }

  const minted = await createAuthCode({
    redirectUri: uri,
    codeChallenge: ctx.codeChallenge!,
    codeChallengeMethod: ctx.codeChallengeMethod ?? 'S256',
    clientId: ctx.clientId!,
    state: ctx.state,
    path: authCodePath,
  });

  const url = new URL(uri);
  url.searchParams.set('code', minted.code);
  if (ctx.state) url.searchParams.set(OAUTH.PARAM.STATE, ctx.state);
  return url.toString();
}

export type PostLoginDestination =
  | { type: 'auth_complete'; redirectUri: string; code: string; state?: string }
  | { type: 'external'; url: string }
  | { type: 'welcome' }
  | { type: 'partner_welcome' }
  | { type: 'error'; message: string };

export async function resolvePostLoginDestination(
  options: { authCodePath?: string } = {},
): Promise<PostLoginDestination> {
  const ctx = loadRedirectContext();
  const authCodePath = options.authCodePath ?? '/auth/code';

  if (ctx.redirectUri) {
    try {
      if (isCustomScheme(ctx.redirectUri)) {
        requirePkce(ctx);
        const minted = await createAuthCode({
          redirectUri: ctx.redirectUri,
          codeChallenge: ctx.codeChallenge!,
          codeChallengeMethod: ctx.codeChallengeMethod ?? 'S256',
          clientId: ctx.clientId!,
          state: ctx.state,
          path: authCodePath,
        });
        return {
          type: 'auth_complete',
          redirectUri: ctx.redirectUri,
          code: minted.code,
          state: ctx.state,
        };
      }
      if (isAllowedHttpsRedirect(ctx.redirectUri)) {
        const url = await buildCodeRedirect(ctx.redirectUri, ctx, authCodePath);
        return { type: 'external', url };
      }
      return { type: 'error', message: 'redirect_uri is not allowlisted' };
    } catch (err) {
      return {
        type: 'error',
        message: err instanceof Error ? err.message : 'Handoff failed',
      };
    }
  }

  let clientType = '';
  try {
    const me = await fetchMe();
    clientType = me.client_type;
  } catch {
    return { type: 'welcome' };
  }

  if (clientType === CLIENT_TYPES.PARTNER) {
    return { type: 'partner_welcome' };
  }

  return { type: 'welcome' };
}

export function navigatePostLogin(
  destination: PostLoginDestination,
  navigate: (path: string, options?: { replace?: boolean }) => void,
): void {
  switch (destination.type) {
    case 'auth_complete': {
      clearRedirectContext();
      const params = new URLSearchParams();
      params.set(OAUTH.PARAM.REDIRECT_URI, destination.redirectUri);
      params.set('code', destination.code);
      if (destination.state) params.set(OAUTH.PARAM.STATE, destination.state);
      clearSession();
      navigate(`/auth/complete?${params.toString()}`, { replace: true });
      break;
    }
    case 'external':
      // Keep portal session so web SPAs (e.g. Core) can return to /welcome without re-login.
      clearRedirectContext();
      window.location.assign(destination.url);
      break;
    case 'error':
      clearRedirectContext();
      navigate(`/login?error=${encodeURIComponent(destination.message)}`, { replace: true });
      break;
    case 'partner_welcome':
      navigate(`/welcome?${PARTNER_QUERY_KEY}=${PARTNER_QUERY_ACTIVE}`, { replace: true });
      break;
    case 'welcome':
    default:
      navigate('/welcome', { replace: true });
      break;
  }
}
